import { identifyUserInSegment } from '@/lib/segment';

type IdentifiableAccount = {
  ssoUUID: string;
  email: string;
};

let identifiedSsoUUID: string | null = null;
let inFlightSsoUUID: string | null = null;
let inFlightPromise: Promise<void> | null = null;

async function generateSha1Hash(input: string | null | undefined): Promise<string> {
  if (!input || !crypto?.subtle) return '';
  try {
    const hashBuffer = await crypto.subtle.digest('SHA-1', new TextEncoder().encode(input));
    return Array.from(new Uint8Array(hashBuffer), (b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return '';
  }
}

function layoutAlreadyIdentified(ssoUUID: string): boolean {
  const accountMeta = document.querySelector('meta[name="account"]') as HTMLMetaElement | null;
  return accountMeta?.dataset?.account === ssoUUID;
}

/**
 * Clears the SPA identify dedupe flag. Call when the authenticated user changes
 * (e.g. logout / registration handoff) so the next account can be identified.
 */
export function resetIdentifiedAccount(): void {
  identifiedSsoUUID = null;
  inFlightSsoUUID = null;
  inFlightPromise = null;
}

/**
 * Identifies the logged-in user in Segment once per SPA lifetime after a confirmed identify.
 * Skips the Vue call when the layout snippet already identified the same user on this page load.
 * Complements the layout snippet so SPA auth handoffs still have an identified user.
 */
export async function identifyAccountInSegmentOnce(account: IdentifiableAccount | null | undefined): Promise<void> {
  if (!account?.ssoUUID) {
    return;
  }

  if (identifiedSsoUUID === account.ssoUUID) {
    return;
  }

  if (layoutAlreadyIdentified(account.ssoUUID)) {
    identifiedSsoUUID = account.ssoUUID;
    return;
  }

  if (inFlightSsoUUID === account.ssoUUID && inFlightPromise) {
    await inFlightPromise;
    return;
  }

  inFlightSsoUUID = account.ssoUUID;
  inFlightPromise = (async () => {
    try {
      const { email } = account;
      const emailSha1 = await generateSha1Hash(email);
      const gbuuid = document.querySelector('meta[name="gbuuid"]')?.getAttribute('content') || undefined;

      const traits: Record<string, string> = { email, emailSha1 };
      if (gbuuid) {
        traits.gbuuid = gbuuid;
      }

      const identified = await identifyUserInSegment(account.ssoUUID, traits);
      if (identified) {
        identifiedSsoUUID = account.ssoUUID;
      }
    } finally {
      if (inFlightSsoUUID === account.ssoUUID) {
        inFlightSsoUUID = null;
        inFlightPromise = null;
      }
    }
  })();

  await inFlightPromise;
}
